import React, {FC, useCallback, useState} from "react";
import AndroidIcon from "@mui/icons-material/Android";
import GetAppIcon from "@mui/icons-material/GetApp";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {Capacitor} from "@capacitor/core";
import {
	AppUpdate,
	AppUpdateAvailability
} from "@capawesome/capacitor-app-update";
import {makeStyles} from "tss-react/mui";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useCapawesomeStoreUpdateOffer} from "@/app2/shared/hooks/useCapawesomeStoreUpdateOffer";

/**
 * Fixed pixel metrics so spacing and scale stay consistent across breakpoints
 * (same visual proportion on small and large screens; not tied to theme.spacing()).
 */
const METRICS_PX = {
	insetHorizontal: 24,
	iconWrapPaddingTop: 16,
	iconWrapPaddingBottom: 4,
	iconFontSize: 48,
	contentPaddingBottom: 20,
	actionsGap: 16,
	actionsPaddingBottom: 24,
	buttonHeight: 46,
	buttonPaddingHorizontal: 24,
	pillBorderRadius: 150
} as const;

const useStyles = makeStyles()((theme) => ({
	dialogPaper: {
		backgroundColor: theme.palette.background.paperElevation3,
		borderColor: theme.palette.background.paperElevation3
	},
	iconWrap: {
		display: "flex",
		justifyContent: "center",
		paddingTop: `${METRICS_PX.iconWrapPaddingTop}px`,
		paddingBottom: `${METRICS_PX.iconWrapPaddingBottom}px`
	},
	storeIcon: {
		fontSize: `${METRICS_PX.iconFontSize}px`,
		color: theme.palette.primary.main
	},
	dialogTitle: {
		paddingLeft: `${METRICS_PX.insetHorizontal}px`,
		paddingRight: `${METRICS_PX.insetHorizontal}px`
	},
	dialogContent: {
		paddingTop: 0,
		paddingBottom: `${METRICS_PX.contentPaddingBottom}px`,
		paddingLeft: `${METRICS_PX.insetHorizontal}px`,
		paddingRight: `${METRICS_PX.insetHorizontal}px`
	},
	dialogActions: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		justifyContent: "flex-start",
		width: "100%",
		maxWidth: "100%",
		boxSizing: "border-box",
		gap: `${METRICS_PX.actionsGap}px`,
		paddingLeft: `${METRICS_PX.insetHorizontal}px`,
		paddingRight: `${METRICS_PX.insetHorizontal}px`,
		paddingBottom: `${METRICS_PX.actionsPaddingBottom}px`,
		paddingTop: 0,
		margin: 0,
		"& > :not(:first-of-type)": {
			marginLeft: 0
		}
	},
	updateNowButton: {
		borderRadius: `${METRICS_PX.pillBorderRadius}px`,
		height: `${METRICS_PX.buttonHeight}px`,
		width: "100%",
		minWidth: 0,
		boxSizing: "border-box",
		color: palette.white,
		fontWeight: 600,
		"&:hover": {
			color: palette.white,
			backgroundColor: palette.orange
		}
	},
	maybeLaterButton: {
		borderRadius: `${METRICS_PX.pillBorderRadius}px`,
		height: `${METRICS_PX.buttonHeight}px`,
		width: "100%",
		minWidth: 0,
		boxSizing: "border-box",
		paddingLeft: `${METRICS_PX.buttonPaddingHorizontal}px`,
		paddingRight: `${METRICS_PX.buttonPaddingHorizontal}px`,
		border: `1px solid ${palette.orange}`,
		color: palette.white,
		backgroundColor: "transparent",
		"&:hover": {
			border: `1px solid ${palette.orange}`,
			color: palette.white,
			backgroundColor: palette.orange08
		}
	}
}));

function StorePlatformIcon({className}: {className: string}) {
	const platform = Capacitor.getPlatform();
	if (platform === "ios") {
		return <PhoneIphoneIcon className={className} aria-hidden />;
	}
	if (platform === "android") {
		return <AndroidIcon className={className} aria-hidden />;
	}
	return <GetAppIcon className={className} aria-hidden />;
}

export interface StoreUpdateAvailableModalProps {
	open: boolean;
	onUpdate: () => void;
	onDismiss: () => void;
}

/**
 * Prompts the learner to update the native app when a newer store version exists.
 * Pair with `StoreUpdateAvailableModalHost` or pass `open` / callbacks from custom logic.
 */
export const StoreUpdateAvailableModal: FC<StoreUpdateAvailableModalProps> = ({
	open,
	onUpdate,
	onDismiss
}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();

	const handleDialogClose = useCallback(
		(_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
			if (reason === "backdropClick" || reason === "escapeKeyDown") {
				onDismiss();
			}
		},
		[onDismiss]
	);

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby="store-update-dialog-title"
			aria-describedby="store-update-dialog-description"
			data-test-id="store-update-available-modal"
			PaperProps={{
				className: classes.dialogPaper
			}}
		>
			<Box className={classes.iconWrap}>
				<StorePlatformIcon className={classes.storeIcon} />
			</Box>
			<DialogTitle id="store-update-dialog-title" className={classes.dialogTitle}>
				{t("store_update_modal_title", "New Update Available")}
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<DialogContentText
					id="store-update-dialog-description"
					variant="body1"
					color="text.primary"
				>
					{t(
						"store_update_modal_subtitle",
						"We've made some exciting improvements on app! Update now to enjoy the latest features."
					)}
				</DialogContentText>
			</DialogContent>
			<DialogActions disableSpacing className={classes.dialogActions}>
				<Button
					variant="contained"
					color="primary"
					fullWidth
					disableElevation
					className={classes.updateNowButton}
					onClick={onUpdate}
					data-test-id="store-update-modal-update-btn"
				>
					{t("store_update_modal_update_now", "Update Now")}
				</Button>
				<Button
					variant="outlined"
					color="primary"
					fullWidth
					className={classes.maybeLaterButton}
					onClick={onDismiss}
					data-test-id="store-update-modal-dismiss-btn"
				>
					{t("store_update_modal_maybe_later", "Maybe Later")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Shows the soft-update modal on native when Capawesome reports an update from the stores.
 */
export const StoreUpdateAvailableModalHost: FC = () => {
	const {updateAvailable} = useCapawesomeStoreUpdateOffer();
	const [dismissed, setDismissed] = useState(false);

	const open = Boolean(updateAvailable && !dismissed);

	const handleUpdate = useCallback(async () => {
		if (!Capacitor.isNativePlatform()) {
			return;
		}
		try {
			if (Capacitor.getPlatform() === "android") {
				const info = await AppUpdate.getAppUpdateInfo();
				if (
					info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE &&
					info.immediateUpdateAllowed
				) {
					await AppUpdate.performImmediateUpdate();
					return;
				}
			}
			await AppUpdate.openAppStore();
		} catch {
			// Play / Store unavailable or user cancelled; avoid surfacing raw errors here
		}
	}, []);

	const handleDismiss = useCallback(() => {
		setDismissed(true);
	}, []);

	return (
		<StoreUpdateAvailableModal open={open} onUpdate={handleUpdate} onDismiss={handleDismiss} />
	);
};
